// src/pages/auth/LoginPage.jsx
// ============================================================
// FIX SUMMARY:
//   1. useEffect is declared UNCONDITIONALLY at the top — not
//      after any early return or inside a condition block.
//      This directly fixes "React Hook called conditionally".
//   2. The early-return redirect check is now in the useEffect,
//      not as an if-block before hooks.
//   3. Loading overlay covers the form while auth state resolves,
//      preventing flicker.
//   4. setIsLoading(false) always runs (finally block) — no
//      hanging spinner on error.
//   5. FIX: getDashboardPath now receives the FULL userProfile
//      object (not just userProfile.role) so getEffectiveRole()
//      can correctly detect Finance-department employees and
//      route them to /finance/dashboard instead of falling back
//      to /employee/dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getDashboardPath } from "../../components/common/ProtectedRoute";

export default function LoginPage() {
  const { login, resetPassword, currentUser, userProfile, loading, deactivatedError, setDeactivatedError } = useAuth();
  const navigate = useNavigate();

  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showReset,    setShowReset]    = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError,    setFormError]    = useState("");
  const [resetMsg,     setResetMsg]     = useState("");
  const [fieldErrors,  setFieldErrors]  = useState({});

  // ── FIX: useEffect is ALWAYS called here, never conditionally ──
  // This redirects an already-logged-in user to their dashboard.
  // Putting this in useEffect (rather than as an early return above
  // the hooks) ensures hooks are never skipped.
  useEffect(() => {
    if (!loading && currentUser && userProfile) {
      // Never redirect a deactivated account
      if (
        deactivatedError ||
        userProfile.isActive === false || 
        userProfile.status === "Inactive" || 
        userProfile.status === "Deactivated" ||
        userProfile.status === "Suspended"
      ) {
        return;
      }
      // FIX: pass the full userProfile object (not userProfile.role)
      // so getDashboardPath -> getEffectiveRole can correctly detect
      // derived roles like "finance" (employee + department === "Finance").
      navigate(getDashboardPath(userProfile), { replace: true });
    }
  }, [loading, currentUser, userProfile, navigate, deactivatedError]);

  // ── Handle login form submit ─────────────────────────────
  async function handleLogin(e) {
    e.preventDefault();
    setFormError("");
    setFieldErrors({});
    setDeactivatedError("");

    const errors = {};
    if (!email.trim()) errors.email = "Email is required.";
    if (!password) errors.password = "Password is required.";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    if (!email.trim() || !password) {
      setFormError("Both email and password are required.");
      setIsSubmitting(false);
      return;
    }
    try {
      await login(email.trim(), password);
      // onAuthStateChanged in AuthContext will fire, update userProfile,
      // and the useEffect above will trigger the redirect automatically.
    } catch (err) {
      if (err.code === "auth/user-deactivated") {
        // deactivatedError is already set in AuthContext; nothing extra needed
      } else if (err.code === "auth/user-not-found" || err.code === "auth/invalid-email") {
        setFieldErrors({ email: "Invalid email address." });
      } else if (err.code === "auth/wrong-password") {
        setFieldErrors({ password: "Invalid password. Please try again." });
      } else if (err.code === "auth/invalid-credential") {
        setFieldErrors({ password: "Invalid email or password." });
      } else {
        setFormError(friendlyError(err.code));
      }
    } finally {
      // Always release the button — even on success (component may
      // unmount from redirect before this runs, which is fine)
      setIsSubmitting(false);
    }
  }

  // ── Handle forgot-password form submit ───────────────────
  async function handleReset(e) {
    e.preventDefault();
    setFormError("");
    setResetMsg("");
    setFieldErrors({});

    if (!email.trim()) {
      setFieldErrors({ email: "Email is required." });
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(email.trim());
      setResetMsg("Reset email sent! Check your inbox (and spam folder).");
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/invalid-email") {
        setFieldErrors({ email: "Invalid email address." });
      } else {
        setFormError(friendlyError(err.code));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // ── While auth is still initialising, show full-screen loader ──
  // This prevents the login form from flashing before we know
  // the user is already logged in.
  if (loading) {
    return (
      <div className="hrms-loading-screen">
        <div className="hrms-spinner" />
        <p className="hrms-loading-msg">Loading…</p>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="login-page">
      {/* ── Left branding panel ──────────────────────────── */}
      <div className="login-brand">
        <div className="login-brand-inner">
          <div className="login-logo">
  <img
    src="/logo.png"
    alt="Samriddhi Anveshana Logo"
    className="login-logo-image"
  />
</div>
          <h1 className="login-brand-name">Samriddhi Anveshana</h1>
          <p className="login-brand-tagline">Employee Management System</p>
          <div className="login-brand-features">
  {[
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="white"
          className="size-6"
          width="22"
          height="22"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
          />
        </svg>
      ),
      text: "Unified workforce management",
    },

    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="white"
          className="size-6"
          width="22"
          height="22"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
          />
        </svg>
      ),
      text: "Real-time attendance tracking",
    },

    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="white"
          className="size-6"
          width="22"
          height="22"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75"
          />
        </svg>
      ),
      text: "Task & project management",
    },

    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="white"
          className="size-6"
          width="22"
          height="22"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
          />
        </svg>
      ),
      text: "Role-based secure access",
    },
  ].map(({ icon, text }) => (
    <div className="login-feature-item" key={text}>
      <span className="login-feature-icon">{icon}</span>
      <span>{text}</span>
    </div>
  ))}
</div>
        </div>
      </div>

      {/* ── Right form panel ─────────────────────────────── */}
      <div className="login-form-panel">
        <div className="login-form-card">
          <h2 className="login-form-title">
            {showReset ? "Reset Password" : "Welcome back"}
          </h2>
          <p className="login-form-subtitle">
            {showReset
              ? "Enter your email and we'll send you a reset link."
              : "Sign in to access your portal."}
          </p>

          {/* Feedback banners */}
          {deactivatedError ? (
            <div className="login-deactivated-banner" role="alert">
              <span className="login-deactivated-icon">🚫</span>
              <div>
                <strong>Account Deactivated</strong>
                <p>Your account has been deactivated. Please contact the administrator.</p>
              </div>
            </div>
          ) : formError ? (
            <div className="login-error" role="alert">{formError}</div>
          ) : null}
          {resetMsg && (
            <div className="login-success" role="status">{resetMsg}</div>
          )}

          {/* Form — switches between login and reset modes */}
          <form
            onSubmit={showReset ? handleReset : handleLogin}
            className="login-form"
            noValidate
          >
            {/* Email field — shown in both modes */}
            <div className="form-group">
              <label htmlFor="lp-email">Email Address</label>
              <input
                id="lp-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors(prev => ({...prev, email: ""})); }}
                placeholder="you@samriddhianveshana.in"
                autoComplete="email"
                disabled={isSubmitting}
                className={fieldErrors.email ? "input-error" : ""}
              />
              {fieldErrors.email && <div className="form-error">{fieldErrors.email}</div>}
            </div>

            {/* Password field — login mode only */}
            {!showReset && (
              <div className="form-group">
                <label htmlFor="lp-password">Password</label>
                <div className="input-with-icon">
                  <input
                    id="lp-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setFieldErrors(prev => ({...prev, password: ""})); }}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={isSubmitting}
                    className={fieldErrors.password ? "input-error" : ""}
                  />
                  <button
                    type="button"
                    className="input-icon-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
    <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
    <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
  </svg>
) : (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 576 512"
    width="20"
    height="20"
    fill="currentColor"
  >
    <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"/>
  </svg>
)}
                  </button>
                </div>
                {fieldErrors.password && <div className="form-error">{fieldErrors.password}</div>}
              </div>
            )}

            <button
              type="submit"
              className="login-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Please wait…"
                : showReset
                ? "Send Reset Email"
                : "Sign In"}
            </button>
          </form>

          {/* Toggle between login and reset */}
          <div className="login-form-footer">
            {showReset ? (
              <button
                type="button"
                className="login-link-btn"
                onClick={() => { setShowReset(false); setFormError(""); setResetMsg(""); setFieldErrors({}); }}
              >
                ← Back to sign in
              </button>
            ) : (
              <button
                type="button"
                className="login-link-btn"
                onClick={() => { setShowReset(true); setFormError(""); setFieldErrors({}); }}
              >
                Forgot password?
              </button>
            )}
          </div>

          <p className="login-note">
            Don't have an account? Contact your HR Administrator to get access.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Firebase error code → human-readable message ─────────
function friendlyError(code) {
  const map = {
    "auth/user-not-found":       "Invalid email address.",
    "auth/wrong-password":       "Invalid password. Please try again.",
    "auth/invalid-credential":   "Invalid email or password.",
    "auth/invalid-email":        "Invalid email address.",
    "auth/too-many-requests":    "Too many failed attempts. Reset your password or try later.",
    "auth/user-disabled":        "Your account has been deactivated. Please contact the administrator.",
    "auth/user-deactivated":     "Your account has been deactivated. Please contact the administrator.",
    "auth/network-request-failed": "Network error. Check your connection and try again.",
  };
  return map[code] ?? "Something went wrong. Please try again.";
}